import type { AssetTypeIdentifier, AssetTypeStatus, Stored } from "@overmap-ai/common"
import { FieldSchema } from "@overmap-ai/forms"
import { useMemo } from "react"

import type { AssetData } from "../../typings"
import { AssetLabelFilterDef } from "../defs/AssetLabelFilterDef"
import { AssetTypeStatusFilterDef } from "../defs/AssetTypeStatusFilterDef"
import { SubmittedAtFilterDef } from "../defs/SubmittedAtFilterDef"
import { getFilterDefConfigsForFieldSchema, getFilterDefsOfIdentifiers } from "../defs/utils"
import { FilterSchema } from "./FilterSchema"
import type { AnyFilterSchemaDefConfig } from "./typings"
import { createFilterDefConfig } from "./utils"

interface AssetDataFilterSchemaConfig {
	assetTypeIdentifiers: Stored<AssetTypeIdentifier>[]
	assetTypeFieldSchema: FieldSchema
	assetTypeStatuses: Stored<AssetTypeStatus>[]
}

export const getAssetDataFilterSchema = (config: AssetDataFilterSchemaConfig) => {
	const { assetTypeIdentifiers, assetTypeFieldSchema, assetTypeStatuses } = config

	const filterDefs: AnyFilterSchemaDefConfig<AssetData>[] = [
		createFilterDefConfig({
			filterDef: new AssetLabelFilterDef(),
			accessorFn: ({ asset }) => asset.label ?? null,
		}),
		createFilterDefConfig({
			filterDef: new SubmittedAtFilterDef(),
			accessorFn: ({ asset }) => new Date(asset.submitted_at),
		}),
	]

	if (assetTypeIdentifiers.length > 0) {
		filterDefs.push(
			...getFilterDefsOfIdentifiers<Stored<AssetTypeIdentifier>, AssetData>(
				assetTypeIdentifiers,
				(identifier) => (data) => data.assetTypeIdentifierValues[identifier.uuid]?.value ?? null,
			),
		)
	}

	if (assetTypeStatuses.length > 0) {
		filterDefs.push(
			createFilterDefConfig({
				filterDef: new AssetTypeStatusFilterDef(assetTypeStatuses),
				accessorFn: ({ asset }) => asset.status,
			}),
		)
	}

	if (assetTypeFieldSchema.getFields().length > 0) {
		filterDefs.push(
			...getFilterDefConfigsForFieldSchema<AssetData>(assetTypeFieldSchema, (data) => [
				assetTypeFieldSchema.deserializeValues(data.assetTypeFieldValues?.values ?? {}),
				data.assetTypeFieldValuesAttachments,
			]),
		)
	}

	return FilterSchema.create<AssetData>({
		filterDefs,
	})
}

export const useAssetDataFilterSchema = (config: AssetDataFilterSchemaConfig) => {
	const { assetTypeIdentifiers, assetTypeFieldSchema, assetTypeStatuses } = config

	return useMemo(
		() =>
			getAssetDataFilterSchema({
				assetTypeIdentifiers,
				assetTypeFieldSchema,
				assetTypeStatuses,
			}),
		[assetTypeFieldSchema, assetTypeIdentifiers, assetTypeStatuses],
	)
}
